import { baseApiPath, deleteEntity, get, post } from "./base-api";
import {
  ICheckAccessCodeParams,
  IRegisterParams,
  IRefreshTokenParams,
  UserStatus,
  ITeamMembersResponse,
  IAnalyticsResponse,
  IUser,
  IRefreshTokenResponse,
  IAuthResponse,
  AnalyticsPeriod,
  IProfilesAnalyticsResponse,
  ProfileFilterCriteriaType,
  IGetProposalsResponse,
} from "../data-access";
import { querify } from "../util";

const authenticate = (email: string): Promise<{ data: string }> => {
  return post(`${baseApiPath}/api/auth/login`, { email });
};

const googleAuthenticate = (
  id_token: string | undefined
): Promise<IAuthResponse> => {
  return post(`${baseApiPath}/api/auth/google-auth`, { id_token });
};

const checkAccessCodeValidity = ({
  email,
  access_code,
}: ICheckAccessCodeParams): Promise<IAuthResponse> => {
  return post(`${baseApiPath}/api/auth/access-code-check`, {
    email,
    access_code,
  });
};

const register = ({
  email,
  picture,
  full_name,
  turnstile_token,
}: IRegisterParams): Promise<{ data: string }> => {
  return post(`${baseApiPath}/api/auth/register`, {
    email,
    picture,
    full_name,
    turnstile_token,
  });
};

// First-touch signup attribution (gclid + UTMs). Fire-and-forget right after
// account creation; the backend stores it write-once on the fresh user.
const saveAttribution = (
  attribution: Record<string, string>
): Promise<void> => {
  return post(`${baseApiPath}/api/user/attribution`, attribution);
};

const refreshToken = ({
  refresh_token,
}: IRefreshTokenParams): Promise<{ data: IRefreshTokenResponse }> => {
  return post<IRefreshTokenParams, { data: IRefreshTokenResponse }>(
    `${baseApiPath}/api/auth/refresh`,
    { refresh_token }
  );
};

const getMe = (): Promise<{ data: IUser }> => {
  return get<{ data: IUser }>(`${baseApiPath}/api/user`);
};

const getTeamMembers = (): Promise<ITeamMembersResponse> => {
  return get<ITeamMembersResponse>(`${baseApiPath}/api/team/users`);
};

const inviteTeamMember = (email: string): Promise<{ data: string }> => {
  return post<{ email: string }, { data: string }>(
    `${baseApiPath}/api/team/invite`,
    { email }
  );
};

const resendInvitation = (email: string): Promise<{ data: string }> => {
  return post<{ email: string }, { data: string }>(
    `${baseApiPath}/api/team/resendInvite`,
    { email }
  );
};

const deleteUser = (id: string, isHard: boolean): Promise<string> => {
  const params = isHard ? "?wipeData=1" : "";
  return deleteEntity<string>(`${baseApiPath}/api/team/${id}/delete${params}`);
};

const changeRole = (id: string, role: UserStatus): Promise<{ data: IUser }> => {
  return post<{ role: string }, { data: IUser }>(
    `${baseApiPath}/api/team/${id}/changeRole`,
    { role }
  );
};

const getUserAnalytics = ({
  id,
  period,
}: {
  id: string;
  period: AnalyticsPeriod;
}): Promise<{ data: IAnalyticsResponse }> => {
  const userIdParam = id ? `&user_id=${id}` : "";
  return get<{ data: IAnalyticsResponse }>(
    `${baseApiPath}/api/insights/get-insights?time_period=${period}${userIdParam}`
  );
};

const getUserRangeAnalytics = (params: string): Promise<{ data: IAnalyticsResponse }> => {
  const queryParams = params ? `?${params}` : "";
  return get<{ data: IAnalyticsResponse }>(
    `${baseApiPath}/api/insights/get-insights${queryParams}`
  );
};

const getAnalyticsForProfiles = (
  filterPeriod: ProfileFilterCriteriaType
): Promise<IProfilesAnalyticsResponse> => {
  const queryParams = filterPeriod ? `?time_period=${filterPeriod}` : "";
  return get(
    `${baseApiPath}/api/insights/get-insights-for-uprofiles${queryParams}`
  );
};

const getAnalyticsForProfilesByDate = (
  params: string
): Promise<IProfilesAnalyticsResponse> => {
  const queryParams = params ? `?${params}` : "";
  return get(
    `${baseApiPath}/api/insights/get-insights-for-uprofiles${queryParams}`
  );
};

export interface ISeatPreview {
  proration_amount: number;
  new_total: number;
  currency: string;
  interval: 'monthly' | 'yearly';
  current_seats: number;
  new_seats: number;
  unit_price: number;
}

const previewSeatCost = (): Promise<ISeatPreview> =>
  get<ISeatPreview>(`${baseApiPath}/api/team/preview-seat-cost`);

export interface ISeatRemovalPreview {
  will_reduce_seat: boolean;
  current_seats: number;
  new_seats: number;
  new_total?: number;
  currency?: string;
  interval?: 'monthly' | 'yearly';
  unit_price?: number;
  current_credits_pool?: number;
  credits_per_seat?: number;
  new_credits_pool?: number;
}

const previewSeatRemoval = (userId: string): Promise<ISeatRemovalPreview> =>
  get<ISeatRemovalPreview>(`${baseApiPath}/api/team/${userId}/preview-seat-removal`);

const fullyLogoutUser = (): Promise<void> => {
  return post<{}, void>(`${baseApiPath}/api/auth/logout`, {});
};

const sendFeedback = (data: {
  type: string;
  message: string;
}): Promise<void> => {
  return post(`${baseApiPath}/api/feedback`, data);
};

const getProposals = (args: {
  upwork_user_id: string;
  upwex_user_id: string;
  page: string;
  per_page: number;
  order_by?: string;
  time_period?: ProfileFilterCriteriaType;
  date_from?: string;
  date_to?: string;
}): Promise<IGetProposalsResponse> => {
  const params = querify(args);
  return get(`${baseApiPath}/api/insights/proposals?${params}`);
};

export const usersApi = {
  authenticate,
  googleAuthenticate,
  checkAccessCodeValidity,
  register,
  saveAttribution,
  refreshToken,
  getMe,
  getTeamMembers,
  inviteTeamMember,
  resendInvitation,
  deleteUser,
  changeRole,
  getUserAnalytics,
  fullyLogoutUser,
  getAnalyticsForProfiles,
  getAnalyticsForProfilesByDate,
  getProposals,
  getUserRangeAnalytics,
  previewSeatCost,
  previewSeatRemoval,
  sendFeedback,
};
